import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  Tabs,
  Card,
  Statistic,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Divider,
  Popconfirm,
  message,
  Tooltip,
  DatePicker,
  InputNumber,
  Rate,
  Upload
} from "antd";
import {
  UserOutlined,
  BookOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  PieChartOutlined,
  PhoneOutlined,
  MailOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  TrophyOutlined,
  StarOutlined,
  SearchOutlined,
  UploadOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";

// APIs
import {
  getAllUsers,
  createUser,
  deleteUser,
  editUser,
  getAllCoursesByRole,
  addCourse,
  editCourse,
  deleteCourse,
  getAllPayments,
  getAllInquiries,
  updateInquiryStatus,
  deleteInquiry,
  getAllTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  getAllAcademicPanelMembers,
  createAcademicPanelMember,
  updateAcademicPanelMember,
  deleteAcademicPanelMember,
  issueCertificate,
  getAllCertificates,
  getAllCenters
} from "../../../Constants/Api/functions/function";

import "./style.scss";

const { Option } = Select;

const AdminPanel = () => {
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);

  // Lists Data States
  const [usersList, setUsersList] = useState([]);
  const [coursesList, setCoursesList] = useState([]);
  const [paymentsList, setPaymentsList] = useState([]);
  const [inquiriesList, setInquiriesList] = useState([]);
  const [certificatesList, setCertificatesList] = useState([]);
  const [testimonialsList, setTestimonialsList] = useState([]);
  const [academicPanelList, setAcademicPanelList] = useState([]);
  const [centersList, setCentersList] = useState([]);

  // Search Filters States
  const [searchUser, setSearchUser] = useState("");
  const [searchCourse, setSearchCourse] = useState("");
  const [searchPayment, setSearchPayment] = useState("");
  const [searchInquiry, setSearchInquiry] = useState("");

  // Modals States
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userForm] = Form.useForm();
  const [editingUserId, setEditingUserId] = useState(null);

  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [courseForm] = Form.useForm();
  const [editingCourseId, setEditingCourseId] = useState(null);

  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  const [certForm] = Form.useForm();

  const [isTestimonialModalOpen, setIsTestimonialModalOpen] = useState(false);
  const [testimonialForm] = Form.useForm();
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);

  const [isPanelModalOpen, setIsPanelModalOpen] = useState(false);
  const [panelForm] = Form.useForm();
  const [editingPanelId, setEditingPanelId] = useState(null);

  // Initial Load
  useEffect(() => {
    loadAllData();
  }, [userProfile]);

  const loadAllData = async () => {
    if (!userProfile) return;
    setLoading(true);
    try {
      // 1. Load users (filtered by center if branch admin - defaults to 2)
      const centerFilter = userProfile.role === "admin" ? (userProfile.centerId || 2) : null;
      const allUsers = await getAllUsers(centerFilter);
      if (allUsers) setUsersList(allUsers);

      // 2. Load courses
      const allCourses = await getAllCoursesByRole(userProfile.role, userProfile.id);
      if (allCourses) setCoursesList(allCourses);

      // 3. Load payments
      const allPayments = await getAllPayments();
      if (allPayments) setPaymentsList(allPayments);

      // 4. Load inquiries
      const allInquiries = await getAllInquiries();
      if (allInquiries) setInquiriesList(allInquiries);

      // 5. Load certificates
      const allCerts = await getAllCertificates();
      if (allCerts) setCertificatesList(allCerts);

      // 6. Load testimonials
      const allTestimonials = await getAllTestimonials();
      if (allTestimonials) setTestimonialsList(allTestimonials);

      // 7. Load academic panel
      const allPanel = await getAllAcademicPanelMembers();
      if (allPanel) setAcademicPanelList(allPanel);

      // 8. Load Centers
      const allCenters = await getAllCenters();
      if (allCenters) setCentersList(allCenters);

    } catch (err) {
      console.error("Error loading admin data:", err);
      message.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // --- CRUD HELPERS ---

  // 1. User Management (Students & Lecturers)
  const handleSaveUser = async (values) => {
    setLoading(true);
    try {
      if (userProfile?.role === "admin") {
        values.centerId = userProfile.centerId || 2;
      }
      if (editingUserId) {
        // Edit User
        const payload = { ...values, id: editingUserId };
        const response = await editUser(payload);
        if (response && response.status === 200) {
          message.success("User updated successfully");
          setIsUserModalOpen(false);
          userForm.resetFields();
          setEditingUserId(null);
          loadAllData();
        } else {
          message.error("Failed to update user");
        }
      } else {
        // Create User
        const response = await createUser(values);
        if (response && response.status === 200) {
          message.success("User created successfully");
          setIsUserModalOpen(false);
          userForm.resetFields();
          loadAllData();
        } else {
          message.error("Failed to create user");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Error saving user");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setLoading(true);
    try {
      const response = await deleteUser(userId);
      if (response && response.status === 200) {
        message.success("User deleted successfully");
        loadAllData();
      } else {
        message.error("Failed to delete user");
      }
    } catch (err) {
      console.error(err);
      message.error("Error deleting user");
    } finally {
      setLoading(false);
    }
  };

  // 2. Course Management
  const handleSaveCourse = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        isPublished: values.isPublished ? 1 : 0,
        isAppearOnHome: values.isAppearOnHome ? 1 : 0,
        isShowOnHome: values.isShowOnHome ? 1 : 0,
        createdBy: userProfile.id
      };

      if (editingCourseId) {
        payload.id = editingCourseId;
        const response = await editCourse(payload);
        if (response && response.status === 200) {
          message.success("Course updated successfully");
          setIsCourseModalOpen(false);
          courseForm.resetFields();
          setEditingCourseId(null);
          loadAllData();
        } else {
          message.error("Failed to update course");
        }
      } else {
        const response = await addCourse(payload);
        if (response && response.status === 200) {
          message.success("Course created successfully");
          setIsCourseModalOpen(false);
          courseForm.resetFields();
          loadAllData();
        } else {
          message.error("Failed to create course");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Error saving course");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    setLoading(true);
    try {
      const response = await deleteCourse(courseId);
      if (response && response.status === 200) {
        message.success("Course deleted successfully");
        loadAllData();
      } else {
        message.error("Failed to delete course");
      }
    } catch (err) {
      console.error(err);
      message.error("Error deleting course");
    } finally {
      setLoading(false);
    }
  };

  // 3. Inquiries Management
  const handleToggleInquiryStatus = async (id, currentStatus) => {
    setLoading(true);
    try {
      const nextStatus = currentStatus === "unread" ? "contacted" : "unread";
      const response = await updateInquiryStatus(id, nextStatus);
      if (response && response.success) {
        message.success("Inquiry status updated");
        loadAllData();
      } else {
        message.error(response?.message || "Failed to update inquiry status");
      }
    } catch (err) {
      console.error(err);
      message.error("Error updating status");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInquiry = async (id) => {
    setLoading(true);
    try {
      const response = await deleteInquiry(id);
      if (response && response.success) {
        message.success("Inquiry deleted successfully");
        loadAllData();
      } else {
        message.error(response?.message || "Failed to delete inquiry");
      }
    } catch (err) {
      console.error(err);
      message.error("Error deleting inquiry");
    } finally {
      setLoading(false);
    }
  };

  // 4. Certificates Management
  const handleIssueCert = async (values) => {
    setLoading(true);
    try {
      const payload = {
        StudentID: values.StudentID,
        CourseID: values.CourseID,
        CertificateNumber: values.CertificateNumber,
        CertificateURL: values.CertificateURL || "https://ibas.lk/verify/default-cert.pdf",
        IssueDate: values.IssueDate ? values.IssueDate.format("YYYY-MM-DD") : new Date().toISOString().split("T")[0]
      };
      const response = await issueCertificate(payload);
      if (response && (response.status === 200 || response.data?.success)) {
        message.success("Certificate issued successfully!");
        setIsCertModalOpen(false);
        certForm.resetFields();
        loadAllData();
      } else {
        message.error(response?.data?.message || "Failed to issue certificate");
      }
    } catch (err) {
      console.error(err);
      message.error("Error issuing certificate");
    } finally {
      setLoading(false);
    }
  };

  // 5. Testimonial Management
  const handleSaveTestimonial = async (values) => {
    setLoading(true);
    try {
      if (editingTestimonialId) {
        const response = await updateTestimonial(editingTestimonialId, values);
        if (response && response.success) {
          message.success("Testimonial updated");
          setIsTestimonialModalOpen(false);
          testimonialForm.resetFields();
          setEditingTestimonialId(null);
          loadAllData();
        } else {
          message.error("Failed to update testimonial");
        }
      } else {
        const response = await createTestimonial(values);
        if (response && response.success) {
          message.success("Testimonial created");
          setIsTestimonialModalOpen(false);
          testimonialForm.resetFields();
          loadAllData();
        } else {
          message.error("Failed to create testimonial");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Error saving testimonial");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    setLoading(true);
    try {
      const response = await deleteTestimonial(id);
      if (response && response.success) {
        message.success("Testimonial deleted");
        loadAllData();
      } else {
        message.error("Failed to delete testimonial");
      }
    } catch (err) {
      console.error(err);
      message.error("Error deleting testimonial");
    } finally {
      setLoading(false);
    }
  };

  // 6. Academic Panel Management
  const handleSavePanelMember = async (values) => {
    setLoading(true);
    try {
      if (editingPanelId) {
        const response = await updateAcademicPanelMember(editingPanelId, values);
        if (response && response.success) {
          message.success("Panel member updated");
          setIsPanelModalOpen(false);
          panelForm.resetFields();
          setEditingPanelId(null);
          loadAllData();
        } else {
          message.error("Failed to update panel member");
        }
      } else {
        const response = await createAcademicPanelMember(values);
        if (response && response.success) {
          message.success("Panel member created");
          setIsPanelModalOpen(false);
          panelForm.resetFields();
          loadAllData();
        } else {
          message.error("Failed to create panel member");
        }
      }
    } catch (err) {
      console.error(err);
      message.error("Error saving panel member");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePanelMember = async (id) => {
    setLoading(true);
    try {
      const response = await deleteAcademicPanelMember(id);
      if (response && response.success) {
        message.success("Panel member deleted");
        loadAllData();
      } else {
        message.error("Failed to delete panel member");
      }
    } catch (err) {
      console.error(err);
      message.error("Error deleting panel member");
    } finally {
      setLoading(false);
    }
  };

  // --- DERIVED METRICS ---
  const studentsCount = usersList.filter((u) => u.role === "student").length;
  const instructorsCount = usersList.filter((u) => u.role === "instructor").length;
  const activeCoursesCount = coursesList.filter((c) => c.isPublished).length;
  const unreadInquiriesCount = inquiriesList.filter((i) => i.status === "unread").length;
  const totalRevenue = paymentsList
    .filter((p) => {
      const status = p.paymentStatus?.toLowerCase();
      return status === "success" || status === "paid" || status === "completed";
    })
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  // --- RENDER SECTIONS ---

  // A. Reports Dashboard View
  const renderDashboardTab = () => {
    return (
      <div className="admin-reports-dashboard">
        {/* KPI Cards */}
        <div className="kpi-grid">
          <Card className="glass-kpi blue">
            <Statistic
              title={<span className="kpi-title">Total Students</span>}
              value={studentsCount}
              prefix={<UserOutlined className="kpi-icon" />}
            />
            <div className="kpi-trend">Academic enrolled learners</div>
          </Card>
          <Card className="glass-kpi gold">
            <Statistic
              title={<span className="kpi-title">Active Courses</span>}
              value={activeCoursesCount}
              prefix={<BookOutlined className="kpi-icon" />}
            />
            <div className="kpi-trend">Programs fully published</div>
          </Card>
          <Card className="glass-kpi purple">
            <Statistic
              title={<span className="kpi-title">Lecturers & Staff</span>}
              value={instructorsCount}
              prefix={<TrophyOutlined className="kpi-icon" />}
            />
            <div className="kpi-trend">Eminent panel members</div>
          </Card>
          <Link to="/dashboard/revenue" style={{ textDecoration: 'none', display: 'block' }}>
            <Card className="glass-kpi green" style={{ cursor: 'pointer' }}>
              <Statistic
                title={<span className="kpi-title">Total Payments Revenue</span>}
                value={totalRevenue}
                precision={2}
                suffix="LKR"
                prefix={<DollarOutlined className="kpi-icon" />}
              />
              <div className="kpi-trend">Card & Manual confirmations • View Report</div>
            </Card>
          </Link>
          <Card className="glass-kpi red">
            <Statistic
              title={<span className="kpi-title">Unread Inquiries</span>}
              value={unreadInquiriesCount}
              prefix={<QuestionCircleOutlined className="kpi-icon" />}
            />
            <div className="kpi-trend">Pending contact requests</div>
          </Card>
        </div>

        {/* Visual Charts */}
        <div className="dashboard-charts-row">
          <Card className="chart-card glassmorphic-panel" title="Monthly Payments Collected">
            <div className="custom-chart-wrapper">
              {/* Sleek Custom SVG Curve Chart */}
              <svg viewBox="0 0 500 200" className="futuristic-svg-chart">
                <defs>
                  <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#108ee9" stopOpacity="0.4" />
                    <stop offset="100%" stopColor="#108ee9" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                <path
                  d="M0 150 Q 80 120, 160 140 T 320 80 T 480 60 L 500 60 L 500 200 L 0 200 Z"
                  fill="url(#chartGrad)"
                />
                <path
                  d="M0 150 Q 80 120, 160 140 T 320 80 T 480 60 L 500 60"
                  fill="none"
                  stroke="#108ee9"
                  strokeWidth="3"
                />
                {/* Dots on chart */}
                <circle cx="80" cy="122" r="4" fill="#87d068" />
                <circle cx="160" cy="140" r="4" fill="#87d068" />
                <circle cx="320" cy="80" r="4" fill="#87d068" />
                <circle cx="480" cy="60" r="4" fill="#87d068" />
                <text x="80" y="105" fill="#fff" fontSize="10" textAnchor="middle">Jan</text>
                <text x="160" y="125" fill="#fff" fontSize="10" textAnchor="middle">Feb</text>
                <text x="320" y="65" fill="#fff" fontSize="10" textAnchor="middle">Mar</text>
                <text x="480" y="45" fill="#fff" fontSize="10" textAnchor="middle">Apr</text>
              </svg>
            </div>
          </Card>

          <Card className="chart-card glassmorphic-panel" title="Learning Centers Distribution">
            <div className="centers-stat-list">
              {centersList.map((center, idx) => {
                const count = usersList.filter((u) => u.centerId === center.CenterId).length;
                const percentage = usersList.length ? Math.round((count / usersList.length) * 100) : 0;
                return (
                  <div key={center.CenterId} className="center-stat-row">
                    <span className="center-name">{center.CenterName}</span>
                    <div className="progress-bar-container">
                      <div className="progress-bar-fill" style={{ width: `${percentage}%`, backgroundColor: idx % 2 === 0 ? "#108ee9" : "#87d068" }} />
                    </div>
                    <span className="center-count">{count} Students ({percentage}%)</span>
                  </div>
                );
              })}
              {centersList.length === 0 && <div className="no-centers-msg">No branch centers configured</div>}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // B. Student & Lecturer Tables
  const renderUsersTab = (roleName) => {
    const filteredUsers = usersList.filter(
      (u) =>
        u.role === roleName &&
        (u.name?.toLowerCase().includes(searchUser.toLowerCase()) ||
          u.email?.toLowerCase().includes(searchUser.toLowerCase()) ||
          u.contactNumber?.includes(searchUser))
    );

    const columns = [
      {
        title: "Index",
        dataIndex: "indexNumber",
        key: "indexNumber",
        render: (text) => <Tag color="blue">{text || "N/A"}</Tag>
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (text, record) => (
          <Space>
            <UserOutlined />
            <strong>{text}</strong>
          </Space>
        )
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        render: (text) => (
          <a href={`mailto:${text}`} style={{ color: "#108ee9" }}>
            {text}
          </a>
        )
      },
      {
        title: "Phone",
        dataIndex: "contactNumber",
        key: "contactNumber"
      },
      {
        title: "Learning Center",
        dataIndex: "centerId",
        key: "centerId",
        render: (centerId) => {
          const centerObj = centersList.find((c) => c.CenterId === centerId);
          return <Tag color="gold">{centerObj ? centerObj.CenterName : "Main Center"}</Tag>;
        }
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#faad14" }} />}
              onClick={() => {
                setEditingUserId(record.id);
                userForm.setFieldsValue({
                  name: record.name,
                  email: record.email,
                  contactNumber: record.contactNumber,
                  address: record.address,
                  role: record.role,
                  centerId: record.centerId
                });
                setIsUserModalOpen(true);
              }}
            />
            <Popconfirm
              title="Are you sure you want to delete this user?"
              onConfirm={() => handleDeleteUser(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div className="tab-pane-content">
        <div className="action-row-header">
          <Input
            placeholder={`Search by Name, Email or Phone...`}
            prefix={<SearchOutlined />}
            value={searchUser}
            onChange={(e) => setSearchUser(e.target.value)}
            className="search-bar-input"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingUserId(null);
              userForm.resetFields();
              userForm.setFieldsValue({ 
                role: roleName,
                centerId: userProfile?.role === "admin" ? (userProfile?.centerId || 2) : null
              });
              setIsUserModalOpen(true);
            }}
          >
            Add {roleName === "student" ? "Student" : "Lecturer"}
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 8 }}
          className="glassmorphic-table"
        />
      </div>
    );
  };

  // C. Course Management View
  const renderCoursesTab = () => {
    const q = (searchCourse || "").trim().toLowerCase();
    const filteredCourses = coursesList.filter((c) => {
      if (!q) return true;
      const nameMatch = c.name?.toLowerCase().includes(q);
      const codeMatch = c.courseid?.toLowerCase().includes(q);
      const tagsMatch = c.tags ? c.tags.toLowerCase().includes(q) : false;
      const isPub = c.isPublished === 1 || c.isPublished === true;
      const pubMatch =
        (q === "published" && isPub) ||
        ((q === "draft" || q === "not published" || q === "unpublished") && !isPub);
      return nameMatch || codeMatch || tagsMatch || pubMatch;
    });

    const columns = [
      {
        title: "Course Code",
        dataIndex: "courseid",
        key: "courseid",
        render: (text) => <Tag color="purple">{text}</Tag>
      },
      {
        title: "Course Name",
        dataIndex: "name",
        key: "name",
        render: (text, record) => (
          <Space>
            {record.coverImage ? (
              <img src={record.coverImage} alt="Cover" style={{ width: 40, height: 30, borderRadius: 4, objectFit: "cover" }} />
            ) : (
              <BookOutlined />
            )}
            <strong>{text}</strong>
          </Space>
        )
      },
      {
        title: "Price (LKR)",
        dataIndex: "price",
        key: "price",
        render: (val) => <span>{val === 0 || val === "0.00" ? "Free" : `${parseFloat(val).toLocaleString()} LKR`}</span>
      },
      {
        title: "Installments",
        dataIndex: "installments",
        key: "installments"
      },
      {
        title: "Status",
        dataIndex: "isPublished",
        key: "isPublished",
        render: (isPublished) => (
          <Tag color={isPublished ? "green" : "red"}>{isPublished ? "Published" : "Draft"}</Tag>
        )
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#faad14" }} />}
              onClick={() => {
                setEditingCourseId(record.id);
                courseForm.setFieldsValue({
                  courseid: record.courseid,
                  name: record.name,
                  description: record.description,
                  coverImage: record.coverImage,
                  price: record.price,
                  installments: record.installments,
                  whatsappGroupLink: record.whatsappGroupLink,
                  videoUrl: record.videoUrl,
                  isPublished: record.isPublished === 1 || record.isPublished === true,
                  isAppearOnHome: record.isAppearOnHome === 1 || record.isAppearOnHome === true,
                  isShowOnHome: record.isShowOnHome === 1 || record.isShowOnHome === true
                });
                setIsCourseModalOpen(true);
              }}
            />
            <Popconfirm
              title="Delete this course?"
              onConfirm={() => handleDeleteCourse(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div className="tab-pane-content">
        <div className="action-row-header">
          <Input
            placeholder="Search by Code or Name..."
            prefix={<SearchOutlined />}
            value={searchCourse}
            onChange={(e) => setSearchCourse(e.target.value)}
            className="search-bar-input"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingCourseId(null);
              courseForm.resetFields();
              setIsCourseModalOpen(true);
            }}
          >
            Add Course
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={filteredCourses}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 6 }}
          className="glassmorphic-table"
        />
      </div>
    );
  };

  // D. Payments View
  const renderPaymentsTab = () => {
    const filteredPayments = paymentsList.filter((p) => {
      const studentObj = usersList.find((u) => u.id === p.userId);
      const courseObj = coursesList.find((c) => c.id === p.courseId);
      const searchStr = `${studentObj?.name || ""} ${courseObj?.name || ""} ${p.paymentMethod || ""} ${p.paymentStatus || ""}`.toLowerCase();
      return searchStr.includes(searchPayment.toLowerCase());
    });

    const columns = [
      {
        title: "Student",
        key: "student",
        render: (_, record) => {
          const studentObj = usersList.find((u) => u.id === record.userId);
          return <span>{studentObj ? studentObj.name : "Unknown User"}</span>;
        }
      },
      {
        title: "Course",
        key: "course",
        render: (_, record) => {
          const courseObj = coursesList.find((c) => c.id === record.courseId);
          return <span>{courseObj ? courseObj.name : "Unknown Course"}</span>;
        }
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        render: (amount) => <strong>{parseFloat(amount).toLocaleString()} LKR</strong>
      },
      {
        title: "Method",
        dataIndex: "paymentMethod",
        key: "paymentMethod",
        render: (method) => <Tag color="blue">{method}</Tag>
      },
      {
        title: "Status",
        dataIndex: "paymentStatus",
        key: "paymentStatus",
        render: (status) => {
          const stat = status?.toUpperCase();
          const color = stat === "SUCCESS" || stat === "PAID" || stat === "COMPLETED" ? "green" : stat === "PENDING" ? "orange" : "red";
          return <Tag color={color}>{stat || "COMPLETED"}</Tag>;
        }
      },
      {
        title: "Date",
        dataIndex: "paymentDate",
        key: "paymentDate",
        render: (date) => <span>{date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "N/A"}</span>
      }
    ];

    return (
      <div className="tab-pane-content">
        <div className="action-row-header">
          <Input
            placeholder="Search payments by student, course, status..."
            prefix={<SearchOutlined />}
            value={searchPayment}
            onChange={(e) => setSearchPayment(e.target.value)}
            className="search-bar-input"
          />
        </div>
        <Table
          columns={columns}
          dataSource={filteredPayments}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 8 }}
          className="glassmorphic-table"
        />
      </div>
    );
  };

  // E. Inquiries View
  const renderInquiriesTab = () => {
    const filteredInquiries = inquiriesList.filter(
      (i) =>
        i.name?.toLowerCase().includes(searchInquiry.toLowerCase()) ||
        i.phone?.includes(searchInquiry) ||
        i.course?.toLowerCase().includes(searchInquiry.toLowerCase())
    );

    const columns = [
      {
        title: "Date",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (date) => <span>{date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "N/A"}</span>
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (text) => <strong>{text}</strong>
      },
      {
        title: "Contact Info",
        key: "contact",
        render: (_, record) => (
          <div className="contact-details-td">
            <div><PhoneOutlined /> {record.phone}</div>
            <div><MailOutlined /> {record.email}</div>
          </div>
        )
      },
      {
        title: "Course",
        dataIndex: "course",
        key: "course",
        render: (c) => <Tag color="cyan">{c || "General Inquiry"}</Tag>
      },
      {
        title: "Message",
        dataIndex: "message",
        key: "message",
        width: 300,
        render: (msg) => <div className="message-excerpt">{msg}</div>
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status) => (
          <Tag color={status === "unread" ? "red" : "green"}>{status?.toUpperCase()}</Tag>
        )
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space size="middle">
            <Tooltip title={record.status === "unread" ? "Mark as Contacted" : "Mark as Unread"}>
              <Button
                type="text"
                icon={<CheckOutlined style={{ color: record.status === "unread" ? "#52c41a" : "#8c8c8c" }} />}
                onClick={() => handleToggleInquiryStatus(record.id, record.status)}
              />
            </Tooltip>
            <Popconfirm
              title="Delete this inquiry record?"
              onConfirm={() => handleDeleteInquiry(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div className="tab-pane-content">
        <div className="action-row-header">
          <Input
            placeholder="Search inquiries by guest name, phone, course..."
            prefix={<SearchOutlined />}
            value={searchInquiry}
            onChange={(e) => setSearchInquiry(e.target.value)}
            className="search-bar-input"
          />
        </div>
        <Table
          columns={columns}
          dataSource={filteredInquiries}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 6 }}
          className="glassmorphic-table"
        />
      </div>
    );
  };

  // F. Certificates View
  const renderCertificatesTab = () => {
    const columns = [
      {
        title: "Cert ID / Number",
        dataIndex: "CertificateNumber",
        key: "CertificateNumber",
        render: (text) => <Tag color="blue">{text || "N/A"}</Tag>
      },
      {
        title: "Student Name",
        dataIndex: "studentName",
        key: "studentName",
        render: (text) => <strong>{text}</strong>
      },
      {
        title: "Course",
        dataIndex: "courseName",
        key: "courseName"
      },
      {
        title: "Issue Date",
        dataIndex: "IssueDate",
        key: "IssueDate",
        render: (date) => <span>{date ? dayjs(date).format("YYYY-MM-DD") : "N/A"}</span>
      },
      {
        title: "Certificate URL / PDF",
        dataIndex: "CertificateURL",
        key: "CertificateURL",
        render: (url) => (
          <a href={url} target="_blank" rel="noreferrer" style={{ color: "#108ee9" }}>
            View Document
          </a>
        )
      }
    ];

    return (
      <div className="tab-pane-content">
        <div className="action-row-header">
          <h3 className="section-subtitle-h3">Verify and Award Student Accreditations</h3>
          <Button
            type="primary"
            icon={<SafetyCertificateOutlined />}
            onClick={() => {
              certForm.resetFields();
              setIsCertModalOpen(true);
            }}
          >
            Issue New Certificate
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={certificatesList}
          rowKey="CertificateID"
          loading={loading}
          pagination={{ pageSize: 8 }}
          className="glassmorphic-table"
        />
      </div>
    );
  };

  // G. Website Content Tab View (Testimonials & Academic Panel CRUDs)
  const renderWebsiteContentTab = () => {
    const testColumns = [
      {
        title: "Author Name",
        dataIndex: "name",
        key: "name",
        render: (text) => <strong>{text}</strong>
      },
      {
        title: "Role / Course Completed",
        dataIndex: "role",
        key: "role"
      },
      {
        title: "Feedback Message",
        dataIndex: "feedback",
        key: "feedback",
        width: 350
      },
      {
        title: "Rating",
        dataIndex: "rating",
        key: "rating",
        render: (val) => <Rate disabled defaultValue={val} />
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#faad14" }} />}
              onClick={() => {
                setEditingTestimonialId(record.id);
                testimonialForm.setFieldsValue(record);
                setIsTestimonialModalOpen(true);
              }}
            />
            <Popconfirm title="Delete testimonial?" onConfirm={() => handleDeleteTestimonial(record.id)}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    const panelColumns = [
      {
        title: "Order",
        dataIndex: "orderNumber",
        key: "orderNumber",
        render: (val) => <Tag color="blue">{val}</Tag>
      },
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: (text) => <strong>{text}</strong>
      },
      {
        title: "Designation / Spec",
        dataIndex: "designation",
        key: "designation"
      },
      {
        title: "Description / Curricula",
        dataIndex: "description",
        key: "description",
        width: 300
      },
      {
        title: "Icon / Avatar URL",
        dataIndex: "imageUrl",
        key: "imageUrl",
        render: (url) => <span>{url || "N/A"}</span>
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "#faad14" }} />}
              onClick={() => {
                setEditingPanelId(record.id);
                panelForm.setFieldsValue(record);
                setIsPanelModalOpen(true);
              }}
            />
            <Popconfirm title="Delete panel member?" onConfirm={() => handleDeletePanelMember(record.id)}>
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )
      }
    ];

    return (
      <div className="tab-pane-content flex-content-panels">
        {/* Testimonials Management Card */}
        <Card className="website-content-sub-card glassmorphic-panel" title="Dynamic Client Testimonials Editor">
          <div className="sub-header-actions">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingTestimonialId(null);
                testimonialForm.resetFields();
                setIsTestimonialModalOpen(true);
              }}
            >
              Add Testimonial
            </Button>
          </div>
          <Table
            columns={testColumns}
            dataSource={testimonialsList}
            rowKey="id"
            pagination={{ pageSize: 3 }}
            size="small"
            className="website-table"
          />
        </Card>

        {/* Academic Panel Management Card */}
        <Card className="website-content-sub-card glassmorphic-panel" title="Dynamic Academic Panel & Faculty Editor">
          <div className="sub-header-actions">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingPanelId(null);
                panelForm.resetFields();
                setIsPanelModalOpen(true);
              }}
            >
              Add Faculty / Spec
            </Button>
          </div>
          <Table
            columns={panelColumns}
            dataSource={academicPanelList}
            rowKey="id"
            pagination={{ pageSize: 3 }}
            size="small"
            className="website-table"
          />
        </Card>
      </div>
    );
  };

  // --- TAB ITEMS DECLARATION ---
  const tabItems = [
    {
      key: "dashboard",
      label: (
        <span>
          <PieChartOutlined /> Reports Dashboard
        </span>
      ),
      children: renderDashboardTab()
    },
    {
      key: "students",
      label: (
        <span>
          <UserOutlined /> Student Management
        </span>
      ),
      children: renderUsersTab("student")
    },
    {
      key: "courses",
      label: (
        <span>
          <BookOutlined /> Course Management
        </span>
      ),
      children: renderCoursesTab()
    },
    {
      key: "lecturers",
      label: (
        <span>
          <TrophyOutlined /> Lecturer Management
        </span>
      ),
      children: renderUsersTab("instructor")
    },
    {
      key: "payments",
      label: (
        <span>
          <DollarOutlined /> Payment Management
        </span>
      ),
      children: renderPaymentsTab()
    },
    {
      key: "inquiries",
      label: (
        <span>
          <QuestionCircleOutlined /> Inquiry Management
        </span>
      ),
      children: renderInquiriesTab()
    },
    {
      key: "certificates",
      label: (
        <span>
          <SafetyCertificateOutlined /> Certificate Management
        </span>
      ),
      children: renderCertificatesTab()
    },
    {
      key: "website",
      label: (
        <span>
          <GlobalOutlined /> Website Content Management
        </span>
      ),
      children: renderWebsiteContentTab()
    }
  ];

  return (
    <div className="unified-admin-panel-container">
      <div className="admin-header-glass">
        <div className="header-info-admin">
          <h1>
            Institutional <span className="highlight-blue">Admin Control Center</span>
          </h1>
          <p>
            Welcome, <strong>{userProfile?.name}</strong>. Manage academic streams, track real-time payments, issue verified credentials, and dynamically adjust landing page aesthetics.
          </p>
        </div>
        <div className="header-role-tag">
          {userProfile?.role === "superadmin" ? (
            <span className="role-badge-super">Super Admin</span>
          ) : (
            <span className="role-badge-admin">Branch Admin</span>
          )}
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={tabItems}
        className="admin-control-tabs"
      />

      {/* --- ADD/EDIT USER MODAL --- */}
      <Modal
        title={editingUserId ? "Edit User Record" : "Register New Academic Account"}
        open={isUserModalOpen}
        onCancel={() => setIsUserModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        className="glass-modal"
      >
        <Form form={userForm} layout="vertical" onFinish={handleSaveUser} initialValues={{ centerId: null }}>
          <Form.Item name="name" label="Full Name" rules={[{ required: true, message: "Please input full name" }]}>
            <Input placeholder="Kamal Perera" />
          </Form.Item>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: "email", message: "Please input valid email" }]}>
            <Input placeholder="kamal@gmail.com" />
          </Form.Item>
          {!editingUserId && (
            <Form.Item name="password" label="Temporary Password" rules={[{ required: true, message: "Please input a temporary password" }]}>
              <Input.Password placeholder="Min 6 characters" />
            </Form.Item>
          )}
          <Form.Item name="contactNumber" label="Contact Mobile Number" rules={[{ required: true, message: "Please input contact number" }]}>
            <Input placeholder="e.g. 0771234567" />
          </Form.Item>
          <Form.Item name="address" label="Home Address">
            <Input placeholder="Gampaha, Sri Lanka" />
          </Form.Item>
          <Form.Item name="role" label="Account Access Role" rules={[{ required: true }]}>
            <Select>
              <Option value="student">Student Account</Option>
              <Option value="instructor">Instructor (Lecturer)</Option>
              {userProfile?.role === "superadmin" && <Option value="admin">Branch Administrator</Option>}
            </Select>
          </Form.Item>
          <Form.Item name="centerId" label="Learning Branch Center">
            <Select placeholder="Select training branch center" disabled={userProfile?.role === "admin"}>
              <Option value={null}>Main Campus (Gampaha)</Option>
              {centersList.map((c) => (
                <Option key={c.CenterId} value={c.CenterId}>
                  {c.CenterName}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item className="modal-actions-form">
            <Space>
              <Button type="default" onClick={() => setIsUserModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Account
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- ADD/EDIT COURSE MODAL --- */}
      <Modal
        title={editingCourseId ? "Edit Course Syllabus" : "Establish New Course Program"}
        open={isCourseModalOpen}
        onCancel={() => setIsCourseModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        className="glass-modal"
      >
        <Form form={courseForm} layout="vertical" onFinish={handleSaveCourse}>
          <Form.Item name="courseid" label="Course Identifier Code" rules={[{ required: true, message: "Please input course ID" }]}>
            <Input placeholder="e.g. IBAS-QB-01" />
          </Form.Item>
          <Form.Item name="name" label="Course Public Title" rules={[{ required: true, message: "Please input course title" }]}>
            <Input placeholder="e.g. Diploma in QuickBooks Accounting" />
          </Form.Item>
          <Form.Item name="description" label="Detailed Syllabus Outline">
            <Input.TextArea rows={3} placeholder="Syllabus overview and study metrics..." />
          </Form.Item>
          <Form.Item name="coverImage" label="Cover Image URL">
            <Input placeholder="https://unsplash.com/... or upload" />
          </Form.Item>
          <Form.Item name="price" label="Course Pricing (LKR)" rules={[{ required: true, message: "Please input course price" }]}>
            <InputNumber style={{ width: "100%" }} min={0} placeholder="Price (0 for free programs)" />
          </Form.Item>
          <Form.Item name="installments" label="Max Installments Count" rules={[{ required: true, message: "Please input installment limit" }]}>
            <InputNumber style={{ width: "100%" }} min={1} placeholder="Maximum months installments allowed" />
          </Form.Item>
          <Form.Item name="whatsappGroupLink" label="WhatsApp Peer Group Link">
            <Input placeholder="https://chat.whatsapp.com/..." />
          </Form.Item>
          <Form.Item name="videoUrl" label="Intro Video URL (YouTube)">
            <Input placeholder="https://youtube.com/watch?v=..." />
          </Form.Item>
          <div className="switches-row" style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
            <Form.Item name="isPublished" label="Publish State" valuePropName="checked">
              <Select>
                <Option value={true}>Visible (Active)</Option>
                <Option value={false}>Draft (Hidden)</Option>
              </Select>
            </Form.Item>
            <Form.Item name="isAppearOnHome" label="Home Carousel" valuePropName="checked">
              <Select>
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>
          </div>
          <Form.Item className="modal-actions-form">
            <Space>
              <Button type="default" onClick={() => setIsCourseModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Course Stream
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- ISSUE CERTIFICATE MODAL --- */}
      <Modal
        title="Issue Verified Professional Certificate"
        open={isCertModalOpen}
        onCancel={() => setIsCertModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        className="glass-modal"
      >
        <Form form={certForm} layout="vertical" onFinish={handleIssueCert}>
          <Form.Item name="StudentID" label="Select Student Recipient" rules={[{ required: true, message: "Student is required" }]}>
            <Select showSearch placeholder="Search student name or index..." filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
              {usersList
                .filter((u) => u.role === "student")
                .map((s) => (
                  <Option key={s.id} value={s.id}>
                    {s.name} ({s.email})
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item name="CourseID" label="Select Completed Course" rules={[{ required: true, message: "Course is required" }]}>
            <Select placeholder="Search course syllabus...">
              {coursesList.map((c) => (
                <Option key={c.id} value={c.id}>
                  {c.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="CertificateNumber" label="Verifiable Certificate Number" rules={[{ required: true, message: "Please input Certificate number" }]}>
            <Input placeholder="e.g. CERT-2026-QB-0049" />
          </Form.Item>
          <Form.Item name="CertificateURL" label="Verified URL (PDF Doc Link)">
            <Input placeholder="https://ibas.lk/certificates/verify/CERT-0049.pdf" />
          </Form.Item>
          <Form.Item name="IssueDate" label="Issue Stamp Date">
            <DatePicker style={{ width: "100%" }} defaultValue={dayjs()} />
          </Form.Item>
          <Form.Item className="modal-actions-form">
            <Space>
              <Button type="default" onClick={() => setIsCertModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Issue Accreditation
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- TESTIMONIAL EDIT MODAL --- */}
      <Modal
        title={editingTestimonialId ? "Edit Client Testimonial" : "Add Client Testimonial"}
        open={isTestimonialModalOpen}
        onCancel={() => setIsTestimonialModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        className="glass-modal"
      >
        <Form form={testimonialForm} layout="vertical" onFinish={handleSaveTestimonial}>
          <Form.Item name="name" label="Author Name" rules={[{ required: true, message: "Please input author name" }]}>
            <Input placeholder="e.g. Shalani Wijewardena" />
          </Form.Item>
          <Form.Item name="role" label="Role / Course Completed" rules={[{ required: true, message: "Please input role or completed course name" }]}>
            <Input placeholder="e.g. Advanced Excel & Data Analytics" />
          </Form.Item>
          <Form.Item name="feedback" label="Testimonial Feedback Message" rules={[{ required: true, message: "Please input feedback" }]}>
            <Input.TextArea rows={3} placeholder="User review content..." />
          </Form.Item>
          <Form.Item name="rating" label="Aesthetic Rating Scale" rules={[{ required: true }]}>
            <Rate defaultValue={5} />
          </Form.Item>
          <Form.Item name="avatarUrl" label="Author Avatar Image URL">
            <Input placeholder="https://images.unsplash.com/... or face area link" />
          </Form.Item>
          <Form.Item className="modal-actions-form">
            <Space>
              <Button type="default" onClick={() => setIsTestimonialModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Testimonial
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* --- ACADEMIC PANEL MEMBER MODAL --- */}
      <Modal
        title={editingPanelId ? "Edit Faculty Member" : "Add Faculty / Spec Member"}
        open={isPanelModalOpen}
        onCancel={() => setIsPanelModalOpen(false)}
        footer={null}
        destroyOnClose
        centered
        className="glass-modal"
      >
        <Form form={panelForm} layout="vertical" onFinish={handleSavePanelMember}>
          <Form.Item name="name" label="Faculty Name / Spec Title" rules={[{ required: true, message: "Please input name" }]}>
            <Input placeholder="e.g. Senior Chartered Accountants or Prof. Kamal Alwis" />
          </Form.Item>
          <Form.Item name="designation" label="Designation / Academic Spec" rules={[{ required: true, message: "Please input designation" }]}>
            <Input placeholder="e.g. Overseeing accounting panel or Senior MBA lecturer" />
          </Form.Item>
          <Form.Item name="description" label="Detailed Scope Description" rules={[{ required: true, message: "Please input scope desc" }]}>
            <Input.TextArea rows={3} placeholder="Explain specific institutional responsibilities..." />
          </Form.Item>
          <Form.Item name="imageUrl" label="Faculty Icon (Emoji or Image URL)">
            <Input placeholder="e.g. 🎓 or https://images.unsplash.com/..." />
          </Form.Item>
          <Form.Item name="orderNumber" label="Display Order Priority">
            <InputNumber style={{ width: "100%" }} min={0} placeholder="Priority integer (0 highest)" />
          </Form.Item>
          <Form.Item className="modal-actions-form">
            <Space>
              <Button type="default" onClick={() => setIsPanelModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Faculty Spec
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPanel;
